if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.DEBOUNCE_TIME = 100;
        this.LIVE_REGION_TIMEOUT = 2000;
        
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          layout: this.querySelector('.media-gallery__layout'),
          main: this.querySelector('.media-gallery__main'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), this.DEBOUNCE_TIME));
        this.initializeDots();
        this.setupCustomSwipe();

        if (this.elements.thumbnails) {
          this.setupThumbnailListeners();
          
          if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) {
            this.removeListSemantic();
          }
        }
      }

      setupThumbnailListeners() {
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          const button = mediaToSwitch.querySelector('button');
          if (button) {
            button.addEventListener('click', () => {
              this.setActiveMedia(mediaToSwitch.dataset.target, false);
            });
          }
        });
      }

      onSlideChanged(event) {
        const currentElement = event.detail.currentElement;
        const mediaId = currentElement.dataset.mediaId;

        if (this.elements.thumbnails) {
          const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          this.setActiveThumbnail(thumbnail);
        }

      }

      setActiveMedia(mediaId, prepend = false) {
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
                           this.elements.viewer.querySelector('[data-media-id]');
        
        if (!activeMedia) return;

        this.updateActiveStates(activeMedia);

        if (prepend) {
          this.handlePrepend(mediaId, activeMedia);
        }

        this.handleScrollAndNavigation(activeMedia);
        this.playActiveMedia(activeMedia);
        this.updateThumbnailsAndAnnounce(mediaId, activeMedia);
      }

      updateActiveStates(activeMedia) {
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');
      }

      handlePrepend(mediaId, activeMedia) {
        const parent = activeMedia.parentElement;
        if (parent.firstChild !== activeMedia) {
          parent.prepend(activeMedia);
        }

        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          if (activeThumbnail) {
            const thumbnailParent = activeThumbnail.parentElement;
            if (thumbnailParent.firstChild !== activeThumbnail) {
              thumbnailParent.prepend(activeThumbnail);
            }
          }
        }

        if (this.elements.viewer.slider) {
          this.elements.viewer.resetPages();
        }
      }

      handleScrollAndNavigation(activeMedia) {
        this.preventStickyHeader();
        
        setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          
          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (activeMediaRect.top > -0.5) return;
          
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
      }

      updateThumbnailsAndAnnounce(mediaId, activeMedia) {
        if (!this.elements.thumbnails) return;
        
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        if (activeThumbnail) {
          this.setActiveThumbnail(activeThumbnail);
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails.querySelectorAll('button').forEach((button) => {
          button.removeAttribute('aria-current');
        });
        
        const thumbnailButton = thumbnail.querySelector('button');
        if (thumbnailButton) {
          thumbnailButton.setAttribute('aria-current', true);
        }

        if (this.elements.thumbnails.slider && !this.isElementVisible(thumbnail, this.elements.thumbnails.slider, 10)) {
          this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
        }
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image || !window.accessibilityStrings?.imageAvailable) return;

        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, this.LIVE_REGION_TIMEOUT);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        if (window.pauseAllMedia) {
          window.pauseAllMedia();
        }
        
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia?.loadContent) {
          deferredMedia.loadContent(false);
        }
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (this.stickyHeader) {
          this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
        }
      }

      initializeDots() {
        const dots = this.querySelectorAll('.product-carousel-dot');
        if (!dots.length) return;

        const dotsContainer = this.querySelector('.product-carousel-dots');
        if (dotsContainer) {
          dotsContainer.classList.add('js-initialized');
        }

        this.updateDots(0);

        dots.forEach((dot) => {
          const slideIndex = parseInt(dot.dataset.slideIndex, 10);
          
          dot.addEventListener('click', (e) => {
            e.preventDefault();
            this.updateDots(slideIndex);
            this.goToSlideByIndex(slideIndex);
          });
        });
      }

      goToSlideByIndex(index) {
        const slides = this.elements.viewer.querySelectorAll('.slider__slide[data-media-id]');
        const targetSlide = slides[index];
        
        if (!targetSlide) return;

        if (this.elements.viewer.slider) {
          this.elements.viewer.slider.scrollTo({
            left: targetSlide.offsetLeft,
            behavior: 'smooth'
          });
        }
        
        this.setActiveMedia(targetSlide.dataset.mediaId, false);
      }

      updateDots(activeIndex) {
        const dots = this.querySelectorAll('.product-carousel-dot');
        dots.forEach((dot, index) => {
          const isActive = index === activeIndex;
          dot.classList.toggle('product-carousel-dot--active', isActive);
          if (isActive) {
            dot.setAttribute('aria-current', 'true');
          } else {
            dot.removeAttribute('aria-current');
          }
        });
      }

      setupCustomSwipe() {
        // Only on mobile
        if (this.mql.matches) return;
        
        const slider = this.elements.viewer.slider;
        if (!slider) return;
        
        let startX = 0;
        let startY = 0;
        let isSwipingHorizontally = false;
        
        slider.addEventListener('touchstart', (e) => {
          startX = e.touches[0].clientX;
          startY = e.touches[0].clientY;
          isSwipingHorizontally = false;
        }, { passive: true });
        
        slider.addEventListener('touchmove', (e) => {
          if (!startX || !startY) return;
          
          const deltaX = Math.abs(e.touches[0].clientX - startX);
          const deltaY = Math.abs(e.touches[0].clientY - startY);
          
          // Determine if this is a horizontal swipe
          if (deltaX > deltaY && deltaX > 10) {
            isSwipingHorizontally = true;
            e.preventDefault(); // Prevent scrolling
          }
        }, { passive: false });
        
        slider.addEventListener('touchend', (e) => {
          if (!startX || !isSwipingHorizontally) {
            startX = 0;
            startY = 0;
            return;
          }
          
          const endX = e.changedTouches[0].clientX;
          const deltaX = startX - endX;
          const threshold = 50; // Minimum swipe distance
          
          if (Math.abs(deltaX) > threshold) {
            const currentDot = this.querySelector('.product-carousel-dot--active');
            const allDots = this.querySelectorAll('.product-carousel-dot');
            const currentIndex = Array.from(allDots).indexOf(currentDot);
            
            let newIndex;
            if (deltaX > 0) {
              // Swiped left - go to next slide
              newIndex = Math.min(currentIndex + 1, allDots.length - 1);
            } else {
              // Swiped right - go to previous slide  
              newIndex = Math.max(currentIndex - 1, 0);
            }
            
            if (newIndex !== currentIndex) {
              this.updateDots(newIndex);
              this.goToSlideByIndex(newIndex);
            }
          }
          
          startX = 0;
          startY = 0;
          isSwipingHorizontally = false;
        }, { passive: true });
      }

      isElementVisible(element, slider, offset = 0) {
        const lastVisibleSlide = slider.clientWidth + slider.scrollLeft - offset;
        return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= slider.scrollLeft;
      }

      removeListSemantic() {
        const slider = this.elements.viewer.slider;
        if (!slider) return;
        
        slider.setAttribute('role', 'presentation');
        if (this.elements.viewer.sliderItems) {
          this.elements.viewer.sliderItems.forEach((slide) => {
            slide.setAttribute('role', 'presentation');
          });
        }
      }


    }
  );
}
